import * as React from "react";
import { toast } from "react-toastify";
import { useDeleteBusinessReportOutcomeMutation } from "@/modules/snapshots/businessReports.api";
import { useDeleteSubcollectionMembersMutation } from "@/modules/snapshots/snapshotCollection.api";

interface Input {
  subcollectionId: string | undefined;
  isContractFamilyProjection: boolean;
  selectedSnapshots: string[];
  setSelectedSnapshots: (ids: string[]) => void;
  refetch: () => unknown;
}

/**
 * Bulk delete of the selected snapshots. Inside a sub-collection this only removes the members from it;
 * otherwise the outcomes themselves are deleted.
 */
export const useDeleteSelectedSnapshots = ({
  subcollectionId,
  isContractFamilyProjection,
  selectedSnapshots,
  setSelectedSnapshots,
  refetch,
}: Input) => {
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [deleteBusinessReportOutcome, { isLoading: isDeleting }] = useDeleteBusinessReportOutcomeMutation();
  const [deleteSubcollectionMembers, { isLoading: isRemovingFromSubcollection }] =
    useDeleteSubcollectionMembersMutation();

  const handleDeleteSelected = async () => {
    try {
      if (subcollectionId) {
        await deleteSubcollectionMembers({
          id: subcollectionId,
          business_report_outcome_ids: selectedSnapshots,
          contract_family_projection: isContractFamilyProjection,
        }).unwrap();
        toast.success(`${selectedSnapshots.length} snapshot(s) removed from sub-collection`);
      } else {
        await Promise.all(selectedSnapshots.map(id => deleteBusinessReportOutcome({ id }).unwrap()));
        toast.success(`${selectedSnapshots.length} snapshot(s) deleted successfully`);
      }
      setSelectedSnapshots([]);
      setShowDeleteDialog(false);
      refetch();
    } catch (error) {
      toast.error(
        subcollectionId
          ? "Failed to remove selected snapshots from sub-collection"
          : "Failed to delete selected snapshots",
      );
      console.error("Delete error:", error);
    }
  };

  return {
    showDeleteDialog,
    setShowDeleteDialog,
    handleDeleteSelected,
    isDeleting: isDeleting || isRemovingFromSubcollection,
  };
};
